import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/es';
import 'moment/locale/pt-br';
import { useTranslation } from 'react-i18next';
import {
  SchedulingProps,
  SchedulingWarningsProps,
} from '../SchedulingInterface';
import {
  Box,
  Grid2,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { styles } from './styles';
import { useEffect, useState } from 'react';
import { useAvailableDatesService } from '@/data/services/schedule/useAvailableDateService';
import { AppError } from '@/utils/AppError';
import {
  AppointmentDateDTO,
  AvailableTime,
} from '@/data/DTOs/schedule/AppointmentDateDTO';
import i18next from 'i18next';
import CheckIcon from '@mui/icons-material/Check';
import { mobile } from '@/utils/mobile';
import { DateList } from './DateList';
import { TimeList } from './TimeList';

export const SchedulingDateTime = ({
  setSchedule,
  schedule,
  setLoading,
  setShowWarning,
  setWarningMessage,
  setIsValid,
  setInternalObject,
  internalObject,
  loading,
}: SchedulingProps & SchedulingWarningsProps) => {
  moment.locale(i18next.language);
  const { t } = useTranslation('schedule');
  const { getAvailableDates } = useAvailableDatesService();

  const selectedDate = schedule.date
    ? moment(schedule.date).toDate()
    : new Date();

  const [selectedMonth, setSelectedMonth] = useState(selectedDate);
  const [selectedDay, setSelectedDay] = useState(selectedDate);
  const [selectedTime, setSelectedTime] = useState<AvailableTime>();
  const [allDays, setAllDays] = useState<Date[]>([]);
  const [allTimes, setAllTimes] = useState<AppointmentDateDTO[]>([]);
  const { handleResize } = mobile();
  const [isMobile, setIsMobile] = useState(false);

  //- Executado sempre que `selectedTime` for alterado.
  useEffect(() => {
    setIsValid(selectedTime !== undefined);

    if (selectedTime) {
      const updatedDoctorName =
        internalObject.doctorName ===
        'schedule:screen_choose_doctor_no_prefence'
          ? internalObject.allDoctors?.find(
              (doctor) => doctor.resourceId === selectedTime.resourceId
            )?.name ?? 'Médico não encontrado'
          : internalObject.doctorName;

      const selectedDate = moment(selectedMonth)
        .date(moment(selectedDay).date())
        .format('YYYY-MM-DD');

      setSchedule({
        ...schedule,
        resourceId: selectedTime.resourceId,
        locationId: selectedTime.locationId,
        date: selectedDate,
        time: selectedTime.time ?? '',
        categoryId: selectedTime.categoryId,
      });

      setInternalObject({
        ...internalObject,
        date: moment(selectedDate).toDate(),
        time: moment(selectedTime.time, 'HH:mm').format('hh:mm A'),
        doctorName: updatedDoctorName,
      });
    }
  }, [selectedTime]);

  //- Executado sempre que `selectedDay` for alterado.
  useEffect(() => {
    setIsValid(false);
    setSelectedTime(undefined);
  }, [selectedDay]);

  // - `internalObject.passedDateSelect` deve estar falso (ou seja, não houve seleção manual pelo usuário).
  // - A data salva (`schedule.date`) deve estar presente em `allDays`.
  useEffect(() => {
    if (allDays.length > 0 && !internalObject.passedDateSelect) {
      const savedDate = schedule.date
        ? moment(schedule.date, 'YYYY-MM-DD')
        : null;

      const dateToSelect =
        savedDate &&
        allDays.some(
          (d) =>
            moment(d).format('YYYY-MM-DD') === savedDate.format('YYYY-MM-DD')
        );

      if (dateToSelect && savedDate) {
        setSelectedDay(savedDate.toDate());
      }
    }
  }, [allDays]);

  useEffect(() => {
    handleResize(setIsMobile);
  }, []);

  useEffect(() => {
    setIsValid(false);
    setLoading(true);

    const yearMonth = moment(selectedMonth).format('YYYY-MM');

    const isOnlineAppointment = internalObject.scheduleType === 'Online';

    const isDoctorSelected =
      !!schedule.resourceId &&
      internalObject.doctorName !== 'schedule:screen_choose_doctor_no_prefence';

    const useResourceId = isDoctorSelected ? schedule.resourceId : '';

    const locationIdParam =
      isOnlineAppointment && isDoctorSelected ? undefined : schedule.locationId;
    console.log('locationIdParam', locationIdParam);

    console.log('[FETCH] Buscando horários com:', {
      locationId: locationIdParam,
      yearMonth,
      resourceId: useResourceId,
    });

    getAvailableDates(locationIdParam, yearMonth, useResourceId)
      .then((times) => {
        console.log('[FETCH] Horários disponíveis>>>>:', times);

        const findDate = times.find((item) =>
          moment(selectedDay).isSame(item.date, 'day')
        );

        const scheduleSelectedTime = findDate?.items.find(
          (time) => time.time === schedule.time
        );

        setAllTimes(times);

        if (scheduleSelectedTime) {
          setSelectedTime(scheduleSelectedTime);
        }
      })
      .catch((error: AppError) => {
        setWarningMessage(t(error.message));
        setShowWarning(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedMonth]);

  const dateItem = (date: Date, index: number) => {
    const itemsLength = (findTimeList(date)?.items.length ?? 0) <= 0;

    const isSelected =
      moment(selectedDay).format('YYYY-MM-DD') ===
      moment(date).format('YYYY-MM-DD');

    const m = moment(date).locale(i18next.language);

    return (
      <ListItemButton
        sx={[
          styles.item,
          isSelected ? styles.itemFocused : styles.itemNotFocused,
        ]}
        key={index}
        selected={isSelected}
        onClick={() => setSelectedDay(date)}
        disabled={itemsLength}
      >
        <ListItemText
          sx={isSelected ? styles.listSelectedItem : styles.listNotSelectedItem}
          primary={`${m.date()}`} // Dia do mês
          secondary={
            <Typography sx={styles.dateMonth}>
              {date
                .toLocaleDateString(i18next.language, { weekday: 'short' })
                .replace('.', '')}{' '}
            </Typography>
          }
        />
      </ListItemButton>
    );
  };

  const findTimeList = (date: Date) => {
    const findDate = allTimes.find((times) => {
      return (
        moment(date).format('YYYY-MM-DD') ===
        moment(times.date).format('YYYY-MM-DD')
      );
    });
    return findDate;
  };

  const timeItem = (time: AvailableTime, index: number) => {
    const dateString = `${moment(selectedMonth).format('YYYY-MM')}-${moment(
      selectedDay
    )
      .date()
      .toString()
      .padStart(2, '0')}T${time.time}-04:00`;

    const date = new Date(dateString);
    const localTime = moment(date);

    return (
      <Grid2 size={1} key={index}>
        <ListItemButton
          sx={[
            styles.timeItem,
            time === selectedTime ? styles.itemFocused : styles.itemNotFocused,
          ]}
          selected={time === selectedTime}
          onClick={() => {
            setSelectedTime(time);
          }}
        >
          {time === selectedTime && !isMobile && (
            <CheckIcon sx={styles.listSelectedItem} />
          )}
          <ListItemText
            slotProps={{
              primary: {
                style: styles.timeItemCard,
              },
            }}
            sx={[
              styles.timeItemCard,
              time === selectedTime
                ? styles.listSelectedItem
                : styles.listNotSelectedItem,
            ]}
            primary={localTime.format('hh:mm A')}
          />
        </ListItemButton>
      </Grid2>
    );
  };

  const timeWait = () =>
    loading ? (
      <Box
        display="grid"
        gridTemplateColumns="repeat(4, 1fr)"
        gap={2}
        sx={{ mt: 2 }}
      >
        {Array.from(new Array(20)).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            width="100%"
            height={40}
            sx={{ border: '1px solid #ccc', borderRadius: 1 }}
          />
        ))}
      </Box>
    ) : (
      <TimeList
        allTimes={allTimes}
        selectedDay={selectedDay}
        timeItem={timeItem}
        internalObject={internalObject.passedDateSelect}
      />
    );

  return (
    <Box sx={styles.container}>
      <DateList
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        setAllDays={setAllDays}
        allDays={allDays}
        dateItem={dateItem}
      />
      {timeWait()}
    </Box>
  );
};
